import { useEffect, useState } from "react";

import { Application } from "@core/Application";
import { ShiftRecipe } from "@models/QontactPoint";

export interface EditableDedicatedShift extends ShiftRecipe {
    localId: string;
    isNew?: boolean;
}

interface UseColaboradorDedicatedShiftsViewModelArgs {
    orgID: string;
    collaboratorID?: string;
}

const DEFAULT_TOLERANCE = 0.5;
const DEFAULT_HOUR = "00:00";

export function useColaboradorDedicatedShiftsViewModel({
    orgID,
    collaboratorID,
}: UseColaboradorDedicatedShiftsViewModelArgs) {
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [shifts, setShifts] = useState<EditableDedicatedShift[]>([]);

    const buildInitialShift = (): EditableDedicatedShift => ({
        localId: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        buildingID: "",
        functionalUnitID: "",
        name: "",
        hourFrom: DEFAULT_HOUR,
        hourTo: DEFAULT_HOUR,
        expected: 1,
        tolerance: DEFAULT_TOLERANCE,
        type: "daily",
        configurationData: {
            weekdays: [],
        },
        expirationType: "never",
        startTimestamp: Date.now(),
        isNew: true,
    });

    useEffect(() => {
        if (!orgID || !collaboratorID) {
            setShifts([]);
            return;
        }

        let isMounted = true;

        const fetchData = async () => {
            setLoading(true);
            try {
                const dedicatedShiftService = Application.shared().coreDependencies.userDedicatedShiftsService;
                const fetchedShifts = await dedicatedShiftService.getShiftRecipesForUser(orgID, collaboratorID);

                if (!isMounted) return;

                setShifts(
                    fetchedShifts.map((shift) => ({
                        ...shift,
                        localId: shift.firestoreRecordId ?? `existing-${Math.random().toString(36).slice(2, 8)}`,
                    }))
                );
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        void fetchData();

        return () => {
            isMounted = false;
        };
    }, [orgID, collaboratorID]);

    const refreshShifts = async () => {
        if (!orgID || !collaboratorID) return;

        setRefreshing(true);
        try {
            const dedicatedShiftService = Application.shared().coreDependencies.userDedicatedShiftsService;
            const fetchedShifts = await dedicatedShiftService.getShiftRecipesForUser(orgID, collaboratorID);

            setShifts(
                fetchedShifts.map((shift) => ({
                    ...shift,
                    localId: shift.firestoreRecordId ?? `existing-${Math.random().toString(36).slice(2, 8)}`,
                }))
            );
        } finally {
            setRefreshing(false);
        }
    };

    const addEmptyShift = () => {
        setShifts((current) => [...current, buildInitialShift()]);
    };

    const removeUnsavedShift = (localId: string) => {
        setShifts((current) => current.filter((shift) => shift.localId !== localId));
    };

    return {
        loading,
        refreshing,
        shifts,
        addEmptyShift,
        removeUnsavedShift,
        refreshShifts,
    };
}
